import { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import CONFIG from "../../config";

export default function useAudioElement(
    setTimeShiftOffsetSec,
    timeShiftOffsetSec,
    setFragProgramDateMs,
    setFragStartSec,
    nowPlaying,
    playbackTimeMs,
    audioTimeMs,
    pausedTimeRef,
    audioRef,
    playbackState,
    setPlaybackState,
) {

    const hlsRef = useRef(null);
    const pausedIntervalRef = useRef(null);

    const hlsUrl = CONFIG.hlsUrl;

    const attachHls = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || hlsRef.current) return;

        if (Hls.isSupported()) {
            const hls = new Hls({
                maxBufferLength: 10,
                maxMaxBufferLength: 30,
                liveSyncDurationCount: 3,
                fragLoadingTimeOut: 15000,
            });
            hlsRef.current = hls;

            hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(hlsUrl));
            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data?.fatal) {
                    console.warn("HLS fatal error:", data);
                    hls.destroy();
                    hlsRef.current = null;
                    // Retry automatically
                    setTimeout(attachHls, 2000);
                }
            });

            hls.on(Hls.Events.FRAG_CHANGED, (_, data) => {
                const frag = data?.frag;
                if (frag) {
                    setFragProgramDateMs(frag.programDateTime ? new Date(frag.programDateTime).getTime() : null);
                    setFragStartSec(typeof frag.start === "number" ? frag.start : 0);
                }
            });

            hls.attachMedia(audio);
        } else {
            audio.src = hlsUrl;
        }
    }, [hlsUrl]);

    const detachHls = () => {
        if (hlsRef.current) {
            try { hlsRef.current.destroy(); } catch { }
            hlsRef.current = null;
        }
    };

    const handlePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (playbackState === "stopped") attachHls();

        audio.play().catch((err) => console.warn("Play interrupted", err));
        setPlaybackState("playing");
    }, [audioRef, playbackState, attachHls]);

    const handlePause = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const liveMs = nowPlaying?.currentLiveTime
            ? new Date(nowPlaying.currentLiveTime).getTime()
            : Date.now();

        const freezeAtMs =
            typeof playbackTimeMs === "number" && playbackTimeMs > 0
                ? playbackTimeMs
                : (audioTimeMs ?? liveMs);

        if (pausedTimeRef) pausedTimeRef.current = playbackTimeMs;

        setTimeShiftOffsetSec((liveMs - freezeAtMs) / 1000);
        setPlaybackState("paused");
    }, [audioRef, nowPlaying, playbackTimeMs, audioTimeMs, pausedTimeRef]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onEnded = () => setPlaybackState("stopped");

        audio.addEventListener("play", handlePlay);
        audio.addEventListener("pause", handlePause);
        audio.addEventListener("ended", onEnded);

        return () => {
            audio.removeEventListener("play", handlePlay);
            audio.removeEventListener("pause", handlePause);
            audio.removeEventListener("ended", onEnded);
        };
    }, [handlePlay, handlePause]);

          useEffect(() => {
        if (pausedIntervalRef.current) {
          clearInterval(pausedIntervalRef.current);
          pausedIntervalRef.current = null;
        }
    
        if (playbackState === "paused") {
          if (pausedTimeRef.current == null) {
            pausedTimeRef.current = playbackTimeMs;
          }
    
          const updateOffset = () => {
            const liveMs = Date.now()
            let newOffset = (liveMs - pausedTimeRef.current) / 1000;
            const maxOffset = nowPlaying?.maxTimeShiftSeconds ?? Infinity;
            if (newOffset > maxOffset) newOffset = maxOffset;
            setTimeShiftOffsetSec(newOffset);
          };
    
          updateOffset();
          pausedIntervalRef.current = setInterval(updateOffset, 1000);
        } else {
          if (pausedIntervalRef.current) {
            clearInterval(pausedIntervalRef.current);
            pausedIntervalRef.current = null;
          }
          if (playbackState === "playing") {
            pausedTimeRef.current = playbackTimeMs - timeShiftOffsetSec * 1000;
          }
    
          if (playbackState === "stopped") {
            pausedTimeRef.current = null;
            setTimeShiftOffsetSec(0);
          }
        }
    
        return () => {
          if (pausedIntervalRef.current) {
            clearInterval(pausedIntervalRef.current);
            pausedIntervalRef.current = null;
          }
        };
      }, [playbackState, nowPlaying?.maxTimeShiftSeconds]);

    return { attachHls, detachHls };
}
